import isBound from "../../form/BoundField/isBound";
import * as React from "react";
import pluginRegistry, {ActionEditProps} from "../pluginRegistry";
import {BaseComponent} from "components/BaseComponent/BaseComponent";
import SpecialVariables from "client/specialVariables";
import {repository} from "clientInstance";
import Roles from "../Roles";
import {ActionSummaryProps} from "../actionSummaryProps";
import {ActionExecutionLocation} from "../../../client/resources/actionExecutionLocation";
import {
    ExpandableFormSection,
    Summary,
    FormSectionHeading,
    Text,
    required,
    Note,
    SummaryNode,
    UnstructuredFormSection
} from "components/form";
import { AccountType, AccountResource} from "client/resources";
import FeedResource, {BuiltInFeedId} from "client/resources/feedResource";
import CommonSummaryHelper from "utils/CommonSummaryHelper/CommonSummaryHelper";
import PackageSelector, {PackageSelectorProperties} from "components/PackageSelector/PackageSelector";
import ExternalLink from "components/Navigation/ExternalLink/ExternalLink";
import { BoundAccountSelect } from "../../form/AccountSelect/AccountSelect";
import RadioButton from "components/form/RadioButton/RadioButton";
import RadioButtonGroup from "components/form/RadioButton/RadioButtonGroup";
import AzureServerTargetRolesInfo from "components/Actions/azurePowerShell/AzureServerTargetRolesInfo";
import AzurePowerShellScriptInfo from "components/Actions/azurePowerShell/AzurePowerShellScriptInfo";
import AzureCloudServiceSelector from "./AzureCloudServiceSelector";
import WarningFormSection from "components/form/Sections/WarningFormSection";
import {getFeedName} from "../getFeedName";
import { TargetRoles } from "areas/projects/components/DeploymentProcess/ActionDetails";
import { BoundSelect } from "components/form/Select/Select";

interface AzureCloudServiceActionSummaryState {
    feedName: string;
}

class AzureCloudServiceActionSummary extends BaseComponent<ActionSummaryProps, AzureCloudServiceActionSummaryState> {
    constructor(props: any) {
        super(props);
        this.state = {feedName: null };
    }

    async componentDidMount() {
        this.setState({feedName: await getFeedName(this.getFeedId())});
    }

    render() {
        return <div>
            Deploy Azure Cloud Service
            <strong> {this.props.properties[SpecialVariables.Action.Package.PackageId]} </strong>
            from {this.state.feedName ? <strong>{this.state.feedName}</strong> : <em>{this.getFeedId()}</em>}
            {this.props.targetRolesAsCSV && <span> on behalf of targets in <Roles rolesAsCSV={this.props.targetRolesAsCSV} /> </span>}
        </div>;
    }

    private getFeedId() {
        return this.props.properties[SpecialVariables.Action.Package.FeedId] as string;
    }
}

interface AzureCloudServiceProperties {
    "Octopus.Action.Package.PackageId": string;
    "Octopus.Action.Package.FeedId": string;
    "Octopus.Action.Azure.SubscriptionId": string;
    "Octopus.Action.Azure.AccountId": string;
    "Octopus.Action.Azure.CloudServiceName": string;
    "Octopus.Action.Azure.StorageAccountName": string;
    "Octopus.Action.Azure.Slot": string;
    "Octopus.Action.Azure.SwapIfPossible": string;
    "Octopus.Action.Azure.UseCurrentInstanceCount": string;
}

interface AzureCloudServiceActionEditState {
    feeds: FeedResource[];
    accounts: AccountResource[];
    account: AccountResource;
    accountIsBound: boolean;
}

class AzureCloudServiceActionEdit extends BaseComponent<ActionEditProps<AzureCloudServiceProperties>, AzureCloudServiceActionEditState> {
    constructor(props: any) {
        super(props);
        this.state = {
            feeds: [],
            accounts: [],
            account: null,
            accountIsBound: isBound(props.properties["Octopus.Action.Azure.AccountId"], false),
        };
    }

    componentDidMount() {
        this.props.doBusyTask(async () => {
            this.setState({
                feeds: await repository.Feeds.all(),
                accounts: await repository.Accounts.all(),
            });

            if (!this.props.properties["Octopus.Action.Package.FeedId"]) {
                this.props.setProperties({ ["Octopus.Action.Package.FeedId"]: BuiltInFeedId }, true);
            }
        });
    }

    accountSummary() {
        const summary = [];
        const accountId = this.props.properties["Octopus.Action.Azure.AccountId"];
        if (accountId) {
            const account = this.state.accounts.find(x => x.Id === accountId);
            if (account) {
                summary.push(<span>Using the <strong>{account.Name}</strong> account</span>);

                (this.props.properties["Octopus.Action.Azure.CloudServiceName"])
                    ? summary.push(<span>, with the <strong>{this.props.properties["Octopus.Action.Azure.CloudServiceName"]}</strong> cloud service</span>)
                    : summary.push(<span>, with <em>(no cloud service selected)</em></span>);

                (this.props.properties["Octopus.Action.Azure.StorageAccountName"])
                    ? summary.push(<span>, and the <strong>{this.props.properties["Octopus.Action.Azure.StorageAccountName"]}</strong> storage account</span>)
                    : summary.push(<span>, and <em>(no storage account selected)</em></span>);

                return Summary.summary(React.Children.toArray(summary));
            }
            if (isBound(accountId, false)) {
                return Summary.summary(<span>Account is bound to <strong>{accountId}</strong></span>);
            }
        }
        return Summary.placeholder("No account has been selected");
    }

    slotSummary() {
        const slot = this.props.properties["Octopus.Action.Azure.Slot"];
        if (slot) {
            return Summary.summary(<span>Using the {slot} deployment slot</span>);
        }
        return Summary.placeholder("Deployment slot not specified");
    }

    swapSummary() {
        const swap = this.props.properties["Octopus.Action.Azure.SwapIfPossible"];
        if (swap === "False") {
            return Summary.summary("When deploying to production always perform a new deployment");
        }
        if (swap === "True") {
            return Summary.summary("When deploying to production swap staging to production, if possible");
        }
        return Summary.placeholder("Swap setting not specified");
    }

    instanceCountSummary() {
        const instanceCount = this.props.properties["Octopus.Action.Azure.UseCurrentInstanceCount"];
        if (instanceCount === "True") {
            return Summary.summary("Using the instance count from the current Azure deployment");
        }
        if (instanceCount === "False") {
            return Summary.summary("Using the instance count defined in the service configuration XML file");
        }
        return Summary.placeholder("Instance count not specified");
    }

    getAccountId = () => {
        return isBound(this.props.properties["Octopus.Action.Azure.AccountId"]) ? undefined : this.props.properties["Octopus.Action.Azure.AccountId"];
    }

    render() {
        const properties = this.props.properties;
        const account = this.state.accounts.find(a => a.Id === properties["Octopus.Action.Azure.AccountId"]);
        const help = this.state.feeds.length > 0
        ? <span>
            This step is used to deploy the contents of a package.
            The package that you wish to deploy should contain all the files needed to run your application. Learn
            more about <ExternalLink href="DocumentationPackaging">what your packages should contain, and how to create them</ExternalLink>.
        </span>
        : <span>Choose the package you which to deploy</span>;

        return <div>
            <WarningFormSection>
                {this.props.additionalActions && <AzureServerTargetRolesInfo stepTargetRoles={this.props.additionalActions.stepTargetRoles}/>}
                <AzurePowerShellScriptInfo actionType={this.props.plugin.actionType}/>
            </WarningFormSection>

            <FormSectionHeading title="Package"  />
            <ExpandableFormSection
                errorKey="Octopus.Action.Package.PackageId|Octopus.Action.Package.FeedId"
                title="Package"
                summary={CommonSummaryHelper.packageSummary(this.props.properties, this.state.feeds)}
                help={help}>
                <PackageSelector
                    setProperties={this.props.setProperties}
                    properties={this.props.properties}
                    projectId={this.props.projectId}
                    feeds={this.state.feeds}
                    getFieldError={this.props.getFieldError}
                    localNames={this.props.localNames}
                    errors={this.props.errors} />
            </ExpandableFormSection>

            <FormSectionHeading title="Azure"  />
            <ExpandableFormSection
                errorKey="Octopus.Action.Azure.AccountId"
                title="Account"
                summary={this.accountSummary()}
                help={"Select the account to use for the connection. Only Azure management-certificate accounts may be selected."}>
                {(this.props.properties["Octopus.Action.Azure.SubscriptionId"] && !this.props.properties["Octopus.Action.Azure.AccountId"]) &&
                <UnstructuredFormSection>
                    <Note>This step is using a subscription ID value imported from Octopus Deploy 2.
                        This value, along with the default Azure certificate, will be used until an Account is selected below.
                        From then on the subscription details from the selected Account will be used.</Note>
                    <Text
                        value={this.props.properties["Octopus.Action.Azure.SubscriptionId"]}
                        label="Subscription"
                        disabled={true}
                        onChange={null}/>
                </UnstructuredFormSection>}
                <BoundAccountSelect
                    variableLookup={{
                        localNames: this.props.localNames,
                        projectId: this.props.projectId
                    }}
                    resetValue={properties["Octopus.Action.Azure.AccountId"]}
                    label="Account"
                    value={properties["Octopus.Action.Azure.AccountId"]}
                    isBound={this.state.accountIsBound}
                    onIsBoundChanged={(value: boolean) => this.setState({accountIsBound: value})}
                    type={[AccountType.AzureSubscription]}
                    allowClear={true}
                    onChange={(x) => this.props.setProperties({["Octopus.Action.Azure.AccountId"]: x})}
                    error={this.props.getFieldError("Octopus.Action.Azure.AccountId")}
                    items={this.state.accounts}
                    onRequestRefresh={this.refreshAccounts} />
                <AzureCloudServiceSelector
                    doBusyTask={this.props.doBusyTask}
                    properties={this.props.properties}
                    setProperties={this.props.setProperties}
                    getFieldError={this.props.getFieldError}
                    projectId={this.props.projectId}
                    accountId={this.getAccountId()}
                    isAccountBound={this.state.accountIsBound}
                    localNames={this.props.localNames} />
            </ExpandableFormSection>

            <FormSectionHeading title="Deployment"  />
            <ExpandableFormSection
                errorKey="Octopus.Action.Azure.Slot"
                title="Slot"
                summary={this.slotSummary()}
                help={<span>Select the deployment slot, usually <em>Production</em> or <em>Staging</em>.</span>}>
                <BoundSelect
                    variableLookup={{
                        localNames: this.props.localNames,
                        projectId: this.props.projectId
                    }}
                    resetValue={""}
                    label="Slot"
                    value={this.props.properties["Octopus.Action.Azure.Slot"]}
                    items={["Staging", "Production"].map(item => ({value: item, text: item}))}
                    onChange={(val) => this.props.setProperties({["Octopus.Action.Azure.Slot"]: val})}
                    error={this.props.getFieldError("Octopus.Action.Azure.Slot")} />
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="Octopus.Action.Azure.SwapIfPossible"
                title="Swap"
                summary={this.swapSummary()}
                help={<span>Azure allows staging and production deployments to be swapped, by switching virtual IP addresses.
                        When deploying to production, Octopus can detect whether the current staging deployment can be swapped,
                        and if so, it can do a swap rather than a new deployment.</span>}>
                <RadioButtonGroup
                    value={this.props.properties["Octopus.Action.Azure.SwapIfPossible"]}
                    onChange={(val: string) => this.props.setProperties({["Octopus.Action.Azure.SwapIfPossible"]: val})}
                    error={this.props.getFieldError("Octopus.Action.Azure.SwapIfPossible")}>
                    <RadioButton value={"False"}
                                 label="Always deploy"/>
                    <RadioButton value={"True"}
                                 label="Swap staging to production if possible"/>
                </RadioButtonGroup>
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="Octopus.Action.Azure.UseCurrentInstanceCount"
                title="Instance Count"
                summary={this.instanceCountSummary()}
                help={<span>If you have previously scaled your service by changing the number of instances using the Azure management portal,
                    Octopus can retrieve these values and use them in the deployment, so that the instance count remains unchanged.</span>}>
                <RadioButtonGroup
                    value={this.props.properties["Octopus.Action.Azure.UseCurrentInstanceCount"]}
                    onChange={(val: string) => this.props.setProperties({["Octopus.Action.Azure.UseCurrentInstanceCount"]: val})}
                    error={this.props.getFieldError("Octopus.Action.Azure.UseCurrentInstanceCount")}>
                    <RadioButton value={"True"}
                                 label="Use the instance count from the current Azure deployment"/>
                    <RadioButton value={"False"}
                                 label="Use the instance count defined in the service configuration XML file"/>
                </RadioButtonGroup>
            </ExpandableFormSection>
        </div>;
    }

    private refreshAccounts = () => {
        return this.props.doBusyTask(async () => {
            this.setState({ accounts: await repository.Accounts.all() });
        });
    }
}

pluginRegistry.registerDeploymentAction({
    executionLocation: ActionExecutionLocation.AlwaysOnServer,
    actionType: "Octopus.AzureCloudService",
    summary: (properties, targetRolesAsCSV) => <AzureCloudServiceActionSummary properties={properties} targetRolesAsCSV={targetRolesAsCSV} />,
    canHaveChildren: (step) => false,
    canBeChild: false,
    edit: AzureCloudServiceActionEdit,
    targetRoleOption: TargetRoles.Optional,
    hasPackages: (action) => true,
    features: {
        optional: ["Octopus.Features.ConfigurationTransforms", "Octopus.Features.ConfigurationVariables", "Octopus.Features.CustomDirectory",
        "Octopus.Features.CustomScripts", "Octopus.Features.JsonConfigurationVariables", "Octopus.Features.SubstituteInFiles"]
    }
});


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/Actions/azureCloudService/azureCloudServiceAction.tsx